import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 數據接口定義
export interface Location {
  area: string;
  floor: number;
  zone?: string;
}

export interface AirQualityData {
  _id: string;
  sensorId: string;
  location: Location;
  measurements: {
    temperature: {
      current: number;
      min: number;
      max: number;
      unit: string;
    };
    humidity: {
      current: number;
      min: number;
      max: number;
      unit: string;
    };
    co2: {
      current: number;
      unit: string;
    };
    pm25: {
      current: number;
      unit: string;
    };
    pm10: {
      current: number;
      unit: string;
    };
  };
  classification: {
    airQualityIndex: number;
    level: 'excellent' | 'good' | 'moderate' | 'poor' | 'hazardous';
    recommendations: string[];
  };
  alerts: Array<{
    type: string;
    severity: 'info' | 'warning' | 'critical';
    message: string;
  }>;
  timestamp: string;
}

export interface LightingSystem {
  _id: string;
  systemId: string;
  location: Location;
  status: {
    isOn: boolean;
    brightness: number;
    mode: 'manual' | 'auto' | 'schedule';
    lastUpdated: string;
  };
  settings: {
    autoMode: {
      enabled: boolean;
      motionSensor: boolean;
      lightSensor: boolean;
      schedule: Array<{
        time: string;
        brightness: number;
        days: string[];
      }>;
    };
    energySaving: {
      enabled: boolean;
      dimAfterMinutes: number;
      offAfterMinutes: number;
    };
  };
  energyConsumption: {
    current: number;
    daily: number;
    monthly: number;
    unit: string;
  };
}

export interface NoiseData {
  _id: string;
  sensorId: string;
  location: Location;
  measurements: {
    decibel: {
      current: number;
      average: number;
      peak: number;
      unit: string;
    };
    frequency: {
      low: number;
      mid: number;
      high: number;
    };
  };
  classification: {
    severity: 'normal' | 'warning' | 'critical';
    type: string;
    description: string;
  };
  thresholds: {
    warning: number;
    critical: number;
  };
  alerts: Array<{
    type: string;
    severity: 'info' | 'warning' | 'critical';
    message: string;
  }>;
  timestamp: string;
}

export interface EnergyData {
  _id: string;
  systemId: string;
  location: Location;
  consumption: {
    current: number;
    daily: number;
    monthly: number;
    yearly: number;
    unit: string;
  };
  efficiency: {
    rating: number;
    trend: 'improving' | 'stable' | 'declining';
    recommendations: string[];
  };
  cost: {
    current: number;
    daily: number;
    monthly: number;
    currency: string;
  };
  timestamp: string;
}

export interface EnvironmentAlert {
  _id?: string;
  type: 'air_quality' | 'noise' | 'energy' | 'lighting';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  location: Location;
  timestamp: string;
  acknowledged?: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  notes?: string;
}

export interface SystemOverview {
  airQuality: {
    totalSensors: number;
    activeSensors: number;
    averageAQI: number;
    alertCount: number;
  };
  lighting: {
    totalSystems: number;
    activeSystems: number;
    energyConsumption: number;
    energySavings: number;
  };
  noise: {
    totalSensors: number;
    activeSensors: number;
    averageLevel: number;
    alertCount: number;
  };
  energy: {
    totalConsumption: number;
    dailyConsumption: number;
    efficiency: number;
    cost: number;
  };
}

export interface StatsQuery {
  area?: string;
  floor?: number;
  period?: 'hourly' | 'daily' | 'weekly' | 'monthly';
  startDate?: string;
  endDate?: string;
}

// API 服務類
class EnvironmentService {
  private apiClient = axios.create({
    baseURL: `${API_BASE_URL}/environment`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // 添加請求攔截器以包含認證令牌
    this.apiClient.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  // 系統總覽
  async getSystemOverview(): Promise<SystemOverview> {
    const response = await this.apiClient.get('/overview');
    return response.data.data;
  }

  // 空氣品質管理
  async getAirQualityData(query?: StatsQuery): Promise<AirQualityData[]> {
    const response = await this.apiClient.get('/air-quality', { params: query });
    return response.data.data;
  }

  async addAirQualityData(data: Partial<AirQualityData>): Promise<AirQualityData> {
    const response = await this.apiClient.post('/air-quality', data);
    return response.data.data;
  }

  async getAirQualityStats(query?: StatsQuery) {
    const response = await this.apiClient.get('/air-quality/stats', { params: query });
    return response.data.data;
  }

  // 照明系統管理
  async getLightingSystems(query?: StatsQuery): Promise<LightingSystem[]> {
    const response = await this.apiClient.get('/lighting', { params: query });
    return response.data.data;
  }

  async controlLighting(systemId: string, action: string, params?: any): Promise<LightingSystem> {
    const response = await this.apiClient.post(`/lighting/${systemId}/control`, {
      action,
      ...params
    });
    return response.data.data;
  }

  async batchControlLighting(systemIds: string[], action: string, params?: any) {
    const response = await this.apiClient.post('/lighting/batch-control', {
      systemIds,
      action,
      ...params
    });
    return response.data.data;
  }

  async optimizeLighting(systemId?: string) {
    const endpoint = systemId ? `/lighting/${systemId}/optimize` : '/lighting/optimize';
    const response = await this.apiClient.post(endpoint);
    return response.data.data;
  }

  async getLightingEnergyStats(query?: StatsQuery) {
    const response = await this.apiClient.get('/lighting/energy-stats', { params: query });
    return response.data.data;
  }

  // 能耗管理
  async getEnergyData(query?: StatsQuery): Promise<EnergyData[]> {
    const response = await this.apiClient.get('/energy', { params: query });
    return response.data.data;
  }

  async addEnergyData(data: Partial<EnergyData>): Promise<EnergyData> {
    const response = await this.apiClient.post('/energy', data);
    return response.data.data;
  }

  async optimizeEnergy(systemId?: string) {
    const endpoint = systemId ? `/energy/${systemId}/optimize` : '/energy/optimize';
    const response = await this.apiClient.post(endpoint);
    return response.data.data;
  }

  async getEnergyTrends(query?: StatsQuery) {
    const response = await this.apiClient.get('/energy/trends', { params: query });
    return response.data.data;
  }

  async setEnergyThresholds(systemId: string, thresholds: any) {
    const response = await this.apiClient.put(`/energy/thresholds/${systemId}`, { thresholds });
    return response.data.data;
  }

  // 噪音監測
  async getNoiseData(query?: StatsQuery): Promise<NoiseData[]> {
    const response = await this.apiClient.get('/noise', { params: query });
    return response.data.data;
  }

  async addNoiseData(data: Partial<NoiseData>): Promise<NoiseData> {
    const response = await this.apiClient.post('/noise', data);
    return response.data.data;
  }

  async controlNoise(sensorId: string, action: string, params?: any) {
    const response = await this.apiClient.post('/noise/control', {
      sensorId,
      action,
      ...params
    });
    return response.data.data;
  }

  async getNoiseStats(query?: StatsQuery) {
    const response = await this.apiClient.get('/noise/stats', { params: query });
    return response.data.data;
  }

  async setNoiseThresholds(sensorId: string, thresholds: any) {
    const response = await this.apiClient.put(`/noise/thresholds/${sensorId}`, { thresholds });
    return response.data.data;
  }

  // 警報管理
  async getAlerts(type?: string, severity?: string, limit?: number): Promise<EnvironmentAlert[]> {
    const response = await this.apiClient.get('/alerts', {
      params: { type, severity, limit }
    });
    return response.data.data;
  }

  async acknowledgeAlert(alertId: string, acknowledgedBy: string, notes?: string) {
    const response = await this.apiClient.put(`/alerts/${alertId}/acknowledge`, {
      acknowledgedBy,
      notes
    });
    return response.data.data;
  }

  // 系統配置
  async getSystemConfig() {
    const response = await this.apiClient.get('/config');
    return response.data.data;
  }

  async updateSystemConfig(systemId: string, config: any) {
    const response = await this.apiClient.put(`/config/${systemId}`, config);
    return response.data.data;
  }
}

export const environmentService = new EnvironmentService();
export default environmentService;